import { Link } from "@/lib/router-compat";
import LegalPageLayout from "@/components/LegalPageLayout";
import { useLanguage, type SiteLang } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";

const sections: Record<SiteLang, { title: string; links: { label: string; to: string }[] }[]> = {
  en: [
    {
      title: "Main Pages",
      links: [
        { label: "Home", to: "/" },
        { label: "About QbD", to: "/en/about-qbd" },
        { label: "Our Story", to: "/en/about-qbd/story" },
        { label: "Ecosystem", to: "/en/about-qbd/ecosystem" },
        { label: "Team", to: "/en/about-qbd/team" },
        { label: "Board of Advisors", to: "/en/about-qbd/board-of-advisors" },
        { label: "QbD Group Foundation", to: "/en/about-qbd/qbd-group-foundation" },
        { label: "Contact", to: "/en/contact" },
      ],
    },
    {
      title: "Services",
      links: [
        { label: "All Services", to: "/en/services" },
        { label: "Regulatory Affairs", to: "/en/services/regulatory-affairs" },
        { label: "Quality Assurance", to: "/en/services/quality-assurance" },
        { label: "Lab Services", to: "/en/services/lab-services" },
        { label: "Qualification & Validation", to: "/en/services/qualification-validation" },
        { label: "Pharmacovigilance", to: "/en/services/pharmacovigilance" },
        { label: "Clinical", to: "/en/services/clinical-solutions" },
        { label: "Regulatory Toxicology", to: "/en/services/regulatory-toxicology" },
        { label: "Software Solutions & Services", to: "/en/services/software-solutions-services" },
      ],
    },
    {
      title: "Solutions",
      links: [{ label: "All Solutions", to: "/en/solutions" }],
    },
    {
      title: "Industries",
      links: [
        { label: "All Industries", to: "/en/industries" },
        { label: "Pharma & Biotech", to: "/en/industries/pharma-biotech" },
        { label: "Medical Devices", to: "/en/industries/medical-devices" },
        { label: "In Vitro Diagnostics", to: "/en/industries/in-vitro-diagnostics" },
      ],
    },
    {
      title: "Knowledge Center",
      links: [
        { label: "Blog", to: "/en/blog" },
        { label: "Webinars", to: "/en/webinars" },
        { label: "Downloads", to: "/en/downloads" },
        // { label: "Podcasts", to: "/en/podcasts" },
        { label: "Case Studies", to: "/en/case-studies" },
        { label: "Regulatory Updates", to: "/en/regulatory-updates" },
        { label: "Events", to: "/en/events" },
        { label: "Company News", to: "/en/news" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Cookie Statement", to: "/en/cookie-statement" },
        { label: "Privacy Policy", to: "/en/privacy-policy" },
        { label: "Terms & Conditions", to: "/en/terms-and-conditions" },
        { label: "Supplier Code of Conduct", to: "/en/supplier-code-of-conduct" },
      ],
    },
  ],
  es: [
    {
      title: "Páginas principales",
      links: [
        { label: "Inicio", to: "/es" },
        { label: "Sobre QbD", to: "/es/about-qbd" },
        { label: "Nuestra historia", to: "/es/about-qbd/story" },
        { label: "Ecosistema", to: "/es/about-qbd/ecosystem" },
        { label: "Equipo", to: "/es/about-qbd/team" },
        { label: "Consejo asesor", to: "/es/about-qbd/board-of-advisors" },
        { label: "Fundación QbD Group", to: "/es/about-qbd/qbd-group-foundation" },
        { label: "Contacto", to: "/es/contact" },
      ],
    },
    {
      title: "Servicios",
      links: [
        { label: "Todos los servicios", to: "/es/servicios" },
        { label: "Regulatory Affairs", to: "/es/servicios/regulatory-affairs" },
        { label: "Quality Assurance", to: "/es/servicios/quality-assurance" },
        { label: "Lab Services", to: "/es/servicios/lab-services" },
        { label: "Qualification & Validation", to: "/es/servicios/qualification-validation" },
        { label: "Pharmacovigilance", to: "/es/servicios/farmacovigilancia" },
        { label: "Clínica", to: "/es/servicios/clinical-solutions" },
        { label: "Toxicología Regulatoria", to: "/es/servicios/toxicologia-regulatoria" },
        { label: "Software Solutions & Services", to: "/es/servicios/software-solutions-services" },
      ],
    },
    {
      title: "Soluciones",
      links: [{ label: "Todas las soluciones", to: "/es/solutions" }],
    },
    {
      title: "Industrias",
      links: [
        { label: "Todas las industrias", to: "/es/industries" },
        { label: "Pharma & Biotech", to: "/es/industries/pharma-biotech" },
        { label: "Medical Devices", to: "/es/industries/medical-devices" },
        { label: "In Vitro Diagnostics", to: "/es/industries/in-vitro-diagnostics" },
      ],
    },
    {
      title: "Recursos",
      links: [
        { label: "Blog", to: "/es/blog" },
        { label: "Webinars", to: "/es/webinars" },
        { label: "Descargas", to: "/es/downloads" },
        { label: "Casos de éxito", to: "/es/casos-de-exito" },
        { label: "Actualizaciones regulatorias", to: "/es/actualizaciones-regulatorias" },
        { label: "Eventos", to: "/es/events" },
        { label: "Noticias", to: "/es/noticias" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Declaración de cookies", to: "/es/cookie-statement" },
        { label: "Política de privacidad", to: "/es/privacy-policy" },
        { label: "Términos y condiciones", to: "/es/terms-and-conditions" },
        { label: "Código de conducta del proveedor", to: "/es/codigo-de-conducta-del-proveedor" },
      ],
    },
  ],
  "zh-cn": [
    {
      title: "主要页面",
      links: [
        { label: "首页", to: "/" },
        { label: "关于QbD", to: "/zh-cn/about-qbd" },
        { label: "联系我们", to: "/zh-cn/contact" },
      ],
    },
    {
      title: "服务",
      links: [{ label: "所有服务", to: "/zh-cn/services" }],
    },
    {
      title: "解决方案",
      links: [{ label: "所有解决方案", to: "/zh-cn/solutions" }],
    },
    {
      title: "行业",
      links: [{ label: "所有行业", to: "/zh-cn/industries" }],
    },
    {
      title: "知识中心",
      links: [
        { label: "博客", to: "/zh-cn/blog" },
        { label: "网络研讨会", to: "/zh-cn/webinars" },
        { label: "下载", to: "/zh-cn/downloads" },
        { label: "案例研究", to: "/zh-cn/case-studies" },
        { label: "新闻", to: "/zh-cn/news" },
      ],
    },
  ],
};

const pageMeta: Record<SiteLang, { title: string; description: string }> = {
  en: { title: "Sitemap", description: "Navigate all pages on the QbD Group website — services, solutions, industries, knowledge center, and more." },
  es: { title: "Mapa del sitio", description: "Navega por todas las páginas del sitio web de QbD Group — servicios, soluciones, industrias, recursos y más." },
  "zh-cn": { title: "网站地图", description: "浏览QbD Group网站的所有页面。" },
};

const SitemapPage = () => {
  const lang = useLanguage();
  const meta = pageMeta[lang];
  const localSections = sections[lang] ?? sections.en;

  return (
    <LegalPageLayout
      title={meta.title}
      description={meta.description}
      path="/sitemap"
    >
      <div className="not-prose grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {localSections.map((section) => (
          <div key={section.title}>
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-3">
              {section.title}
            </h2>
            <ul className="space-y-1.5">
              {section.links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={localizeHref(link.to, lang)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </LegalPageLayout>
  );
};

export default SitemapPage;
